<template>
  <div
    class="fts-automation-designer"
    :class="{ 'automation-designer--expand': fullView }"
  >
    <div class="automation-sidenav">
      <div class="automation-sidenav__control">
        <div
          class="automation-sidenav-menu"
          :class="{ collapsed: controlCollapse }"
          @click="controlCollapse = !controlCollapse"
        >
          <div class="automation-sidenav-menu__label">Try It</div>
          <font-awesome-icon
            :icon="['fas', 'caret-right']"
            fixed-width
            class="automation-sidenav-menu__toggle"
          />
        </div>

        <fts-transition-expand>
          <div v-if="!controlCollapse">
            <div class="automation-debug-selection">
              <div class="automation-debug-select automation-debug--entry">
                <fts-form-label placement="top">Entry</fts-form-label>
                <fts-dropdown
                  :form-data="data"
                  :item="{
                    displayName: 'Test Locus',
                    fieldName: 'testLocus',
                    type: 7,
                    key: 'testLocusId',
                    connectionId: moduleId,
                  }"
                  :module-design="moduleId"
                  @input="onTestLocusInput"
                />
              </div>

              <div
                class="automation-debug-select automation-debug--activity"
                v-if="data.testLocusId"
              >
                <fts-form-label placement="top">Activity</fts-form-label>
                <fts-dropdown
                  :form-data="data"
                  :item="{
                    displayName: 'Test Activity',
                    fieldName: 'testLocusDetail',
                    type: 7,
                    key: 'testLocusDetailId',
                    connectionId: moduleId,
                  }"
                  :module-design="moduleId"
                  locus-detail
                  :locus-id="data.testLocusId"
                >
                  <fts-label-group
                    slot="option"
                    slot-scope="{ option }"
                    fontawesome
                    :item="{
                      icon: activityMatchValidation(activityId, option.activityId) ? null : ['fas', 'exclamation-circle'],
                      name: option.optionName
                    }"
                  />
                </fts-dropdown>
              </div>
            </div>

            <div class="automation-debug-panel">
              <fts-btn icon text @click="debugAutomation(0)">
                <font-awesome-icon :icon="['fas', 'play']" />
              </fts-btn>
              <fts-btn icon text @click="debugAutomation(1)">
                <font-awesome-icon :icon="['fas', 'step-forward']" />
              </fts-btn>
              <fts-btn icon text @click="debugAutomation(2)">
                <font-awesome-icon :icon="['fas', 'stop']" />
              </fts-btn>
            </div>
          </div>
        </fts-transition-expand>
      </div>

      <div class="automation-sidenav__node">
        <div
          class="automation-sidenav-menu"
          :class="{ collapsed: nodeCollapse }"
          @click="nodeCollapse = !nodeCollapse"
        >
          <div class="automation-sidenav-menu__label">Tools</div>
          <font-awesome-icon
            :icon="['fas', 'caret-right']"
            fixed-width
            class="automation-sidenav-menu__toggle"
          />
        </div>

        <fts-transition-expand>
          <div v-if="!nodeCollapse">
            <div
              v-for="node in nodes"
              :key="node.id"
              class="automation-sidenav-node"
              :class="`automation-sidenav-node-${node.theme}`"
              draggable="true"
              :data-node="node.id"
              @dragstart="drag"
            >
              <div 
                class="automation-sidenav-node__icon" 
                v-if="node.icon"
              >
                <i :class="node.icon"></i>
              </div>
              <div class="automation-sidenav-node__label">{{ node.name }}</div>
            </div>
          </div>
        </fts-transition-expand>
      </div>

      <div class="automation-sidenav__console">
        <div
          class="automation-sidenav-menu"
          :class="{ collapsed: consoleCollapse }"
          @click="consoleCollapse = !consoleCollapse"
        >
          <div class="automation-sidenav-menu__label">
            Variables
            <span v-if="debugPayload && debugPayload.debugPayload"
              >({{ Object.keys(debugPayload.debugPayload).length }})</span
            >
          </div>
          <font-awesome-icon
            :icon="['fas', 'caret-right']"
            fixed-width
            class="automation-sidenav-menu__toggle"
          />
        </div>

        <fts-transition-expand>
          <div class="fts-scrollbar" v-if="!consoleCollapse">
            <fts-automation-variables
              v-if="debugPayload && debugPayload.debugPayload"
              :variable="debugPayload.debugPayload"
              style="margin: 0 1rem 1rem"
            />
          </div>
        </fts-transition-expand>
      </div>
    </div>
    <div class="automation-content">
      <div class="automation-content-header">
        <div class="automation-content-header-wrapper automation-native-control">
          <fts-toggle-switch
            v-model="serverNativeEnabled"
            :disabled="serverNativeToggleDisabled"
            mat-class
            :size="15"
            class="automation-native-toggle"
          >
            <div slot="label" class="automation-native-toggle__label">
              Server-native
            </div>
          </fts-toggle-switch>
          <fts-tippy :content="nativeVerdictTooltip" v-if="nativeVerdictLabel">
            <span class="automation-native-badge" :class="nativeVerdictClass">
              {{ nativeVerdictLabel }}
            </span>
          </fts-tippy>
        </div>
        <div class="automation-content-header-wrapper">
          <div class="automation-zoom-control">
            <fts-btn sm text icon @click="editorZoomOut">
              <font-awesome-icon :icon="['fas', 'minus']" />
            </fts-btn>
            <fts-dropdown-select
              v-model="editorZoom"
              :options="editorZoomOptions"
              :reduce="(x) => x.id"
              placement="bottom"
              append-toggle-class="automation-zoom-dropdown-toggle"
              class="automation-zoom-dropdown"
              @selected="updateEditorZoom"
            >
              <template #toggle>{{ editorZoomPercent }}</template>
            </fts-dropdown-select>
            <fts-btn sm text icon @click="editorZoomIn">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </fts-btn>
          </div>
        </div>
        <div class="automation-content-header-wrapper automation-content-header-action">
          <fts-tippy
            content="Exit full screen mode"
            key="window-restore"
            v-if="fullView"
          >
            <fts-btn
              sm
              text
              icon
              @click="toggleLayout(false)"
            >
              <font-awesome-icon :icon="['far', 'window-restore']" />
            </fts-btn>
          </fts-tippy>

          <fts-tippy
            content="Expand to full screen"
            key="window-maximize"
            v-else
          >
            <fts-btn
              sm
              text
              icon
              @click="toggleLayout(true)"
            >
              <font-awesome-icon :icon="['far', 'window-maximize']" />
            </fts-btn>
          </fts-tippy>

          <fts-btn
            sm
            text
            color="primary"
            :loading="isSaving"
            @click="$emit('save')"
            v-if="saveButton && fullView"
          >
            <font-awesome-icon :icon="['fas', 'check']" /> Save
          </fts-btn>
        </div>
      </div>
      <div
        id="drawflow"
        style="perspective: 0; width: 100%; height: 100%;"
        @drop="drop"
        @dragover="allowDrop"
      ></div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import commons from '../../commons/commons';
import automationBlocks from '../../commons/automationBlocks';
import VueJsonPretty from 'vue-json-pretty';
import { automationDesignStore } from '../../store/automationDesignStore';
import { authorizationStore } from '../../store/authorizationStore';

import Drawflow from '../../../external/drawflow';
import styleDrawflow from '../../../external/scss/drawflow.scss';
import FTSAutomationVariables from './FTSAutomationVariables';
import FTSBtn from '../../elements/FTSBtn';
import FTSDropdown from '../../elements/FTSDropdown';
import FTSDropdownSelect from '../../elements/FTSDropdownSelect';
import FTSFormLabel from '../../elements/FTSFormLabel';
import FTSLabelGroup from '../../elements/FTSLabelGroup';
import FTSTippy from '../../elements/FTSTippy';
import FTSToggleSwitch from '../../elements/FTSToggleSwitch';
import FTSTransitionExpand from '../../elements/FTSTransitionExpand';

export default {
  props: {
    data: Object,
    moduleId: String,
    activityId: String,
    isForm: Boolean,
    form: Object,
    saveButton: Boolean,
    isSaving: Boolean,
    activityMatchValidation: {
      type: Function,
      default: (sourceActivityId, targetActivityId) => {
        if (sourceActivityId === "createEditView") {
          return ["create", "edit"].includes(targetActivityId);
        }

        return sourceActivityId === targetActivityId;
      }
    }
  },
  data: () => ({
    exportDataModel: {},
    editor: null,
    editorZoom: 1,
    editorZoomOptions: [
      { id: 0.5, name: "50%" },
      { id: 0.75, name: "75%" },
      { id: 1, name: "100%" },
      { id: 1.25, name: "125%" },
      { id: 1.5, name: "150%" },
      { id: 1.75, name: "175%" },
      { id: 2, name: "200%" },
    ],
    fullView: false,
    /* nodes */
    nodes: [
      {
        id: "sequence",
        name: "Action",
        theme: "action",
        icon: "fts-icon-automation-action",
      },
      {
        id: "ifElse",
        name: "Condition",
        theme: "condition",
        icon: "fts-icon-automation-condition",
      },
      {
        id: "forEach",
        name: "For",
        theme: "for",
        icon: "fts-icon-automation-for",
      },
      {
        id: "trigger",
        name: "Trigger",
        theme: "trigger",
        icon: "fts-icon-automation-action",
      },
    ],
    /* collapse */
    nodeCollapse: false,
    controlCollapse: false,
    consoleCollapse: false,
    /* formData */
    formData: {},
    debugPayload: null,
    /* server-native (advisory verdict from api/canvastest/automationnative) */
    nativeVerdict: null,
    nativeVerdictRefresh: null,
  }),
  components: {
    "fts-automation-variables": FTSAutomationVariables,
    "fts-btn": FTSBtn,
    "fts-dropdown": FTSDropdown,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-form-label": FTSFormLabel,
    "fts-label-group": FTSLabelGroup,
    "fts-tippy": FTSTippy,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-transition-expand": FTSTransitionExpand,
    VueJsonPretty,
  },
  methods: {
    // Master plan (2026-08-01): internal/consultant users may relax a graph to the Python
    // engine after an explicit warning; normal users never reach here (toggle locked).
    async relaxServerNative() {
      if (!this.canRelaxServerNative || !this.data) return;
      const res = await this.$confirmation.show({
        title: "Switch to the Python engine?",
        text: "Server-native is the platform default and the faster engine. Move this automation back to Python only for a confirmed incompatibility or result divergence."
      });
      if (res === true && this.data) {
        this.$delete(this.data, "serverNative");
      }
    },
    async debugAutomation(debugType) {
      if (debugType == 2) {
        if (this.debugPayload) {
          this.debugPayload.nodeId = null;
          this.debugPayload.actionId = null;
          this.debugPayload.debugPayload = null;
          this.debugPayload.forEach = null;
        }

        window.eventBus.$emit("refreshAutomationBlocks");
        return;
      }

      let debugPayloadClone = _.cloneDeep(this.debugPayload);

      try {
        let result = await this.$store.dispatch("testAutomationBlock", {
          vectorId: this.moduleId,
          automationBlocks: this.data,
          debugPayload: debugPayloadClone,
          debug: debugType,
          isForm: this.isForm,
        });

        this.debugPayload = result.data;
      } catch (err) {
        console.error(err);
        this.$toasted.global.error({ response: err.response});
      }
    },
    drag(ev) {
      if (ev.type === "touchstart") {
        mobile_item_selec = ev.target
          .closest(".drag-drawflow")
          .getAttribute("data-node");
      } else {
        ev.dataTransfer.setData("node", ev.target.getAttribute("data-node"));
      }
    },
    drop(ev) {
      let node = ev.dataTransfer.getData("node");
      let editor = this.editor;

      let pos_x =
        ev.clientX *
          (editor.precanvas.clientWidth /
            (editor.precanvas.clientWidth * editor.zoom)) -
        editor.precanvas.getBoundingClientRect().x *
          (editor.precanvas.clientWidth /
            (editor.precanvas.clientWidth * editor.zoom));
      let pos_y =
        ev.clientY *
          (editor.precanvas.clientHeight /
            (editor.precanvas.clientHeight * editor.zoom)) -
        editor.precanvas.getBoundingClientRect().y *
          (editor.precanvas.clientHeight /
            (editor.precanvas.clientHeight * editor.zoom));

      automationBlocks.addNode(
        this.editor,
        node,
        { id: commons.nanoID() },
        pos_x,
        pos_y
      );
    },
    allowDrop(ev) {
      ev.preventDefault();
    },
    exportModel() {
      var exportdata = this.editor.export();
      this.data.blocks = exportdata.drawflow.Home.data;
      this.exportDataModel = _.cloneDeep(this.data);
      Object.values(this.exportDataModel).forEach((node) => {
        if (node.name != "start") {
          delete node.data.variables;
          delete node.data.priorVariables;

          if (node.data.actions) {
            node.data.actions.forEach((action) => {
              delete action.variables;
              delete action.priorVariables;
            });
          }
        }
      });

      return this.exportDataModel;
    },
    async loadDiagram() {
      let vm = this;
      automationDesignStore.dispatch("loadDiagram", vm.data.blocks);
      automationDesignStore.dispatch("loadForm", vm.form);
      //setPlugin
      let pluginsResponse = await this.$store.dispatch("plugins");
      automationDesignStore.dispatch(
        "loadPlugins",
        pluginsResponse.data.plugIns
      );
      // closed while the plugins loaded: beforeDestroy has run, so nothing would destroy an editor built now
      if (this._isBeingDestroyed || this._isDestroyed) return;
      var id = document.getElementById("drawflow");
      try {
        // a re-run builds on the same container: the old editor's nodes, listeners and canvas go first
        if (this.editor) {
          this.editor.destroy();
          this.editor.precanvas && this.editor.precanvas.remove();
        }
        this.editor = new Drawflow(id, Vue, vm);
        this.editor.drawflow = {
          drawflow: {
            Home: {
              data: vm.data.blocks,
            },
          },
        };
        automationBlocks.registerNodes(this.editor);
        this.editor.start();
        this.editor.zoom_max = 2;
        this.editor.draggable_inputs = false;

        this.editor.on("nodeCreated", function (id) {
          console.log("Node created " + id);
          automationDesignStore.dispatch("updateVariables");
          window.eventBus.$emit("refreshAutomationBlocks");
        });

        this.editor.on("nodeRemoved", function (id) {
          console.log("Node created " + id);
          automationDesignStore.dispatch("updateVariables");
          window.eventBus.$emit("refreshAutomationBlocks");
        });

        this.editor.on("connectionCreated", function (id) {
          console.log("Connection created " + id);
          automationDesignStore.dispatch("updateVariables");
          window.eventBus.$emit("refreshAutomationBlocks");
        });

        this.editor.on("connectionRemoved", function (id) {
          console.log("Connection removed " + id);
          // vm.$forceUpdate();
          automationDesignStore.dispatch("updateVariables");
          window.eventBus.$emit("refreshAutomationBlocks");
        });

        window.eventBus.$emit("refreshAutomationBlocks");
      } catch (err) {
        console.log(err);
      }
    },
    onTestLocusInput() {
      Boolean(this.data.testLocusDetailId) &&
        this.$delete(this.data, "testLocusDetailId");
      Boolean(this.data.testLocusDetail) &&
        this.$delete(this.data, "testLocusDetail");
    },
    // Advisory server-native verdict: classify the CURRENT editor graph (without mutating
    // this.data) so the badge tracks unsaved edits. Fired debounced off the same
    // refreshAutomationBlocks signal the node/action editors already emit.
    async refreshNativeVerdict() {
      if (!this.data) {
        this.nativeVerdict = null;
        return;
      }

      let blocks = this.data.blocks;
      if (this.editor) {
        const exported = this.editor.export();
        blocks =
          exported && exported.drawflow && exported.drawflow.Home
            ? exported.drawflow.Home.data
            : blocks;
      }

      if (!blocks || !Object.keys(blocks).length) {
        this.nativeVerdict = null;
        return;
      }

      // same variables/priorVariables strip as exportModel, on a clone
      const snapshot = _.cloneDeep(blocks);
      Object.values(snapshot).forEach((node) => {
        if (node.name != "start" && node.data) {
          delete node.data.variables;
          delete node.data.priorVariables;

          if (node.data.actions) {
            node.data.actions.forEach((action) => {
              delete action.variables;
              delete action.priorVariables;
            });
          }
        }
      });

      try {
        const result = await this.$store.dispatch("classifyAutomationNative", {
          vectorId: this.moduleId,
          activityId: this.activityId,
          isForm: this.isForm,
          automationBlocks: { blocks: snapshot },
        });
        this.nativeVerdict = result.data;
      } catch (err) {
        // advisory only — a failed background check must never toast or block the designer
        this.nativeVerdict = null;
      }

      this.markBlockingNodes();
    },
    // Amber-outline the drawflow nodes that keep the graph off the native lane. Scoped to
    // THIS editor's element (the before/after designers can coexist and drawflow reuses
    // node-<id> ids across instances).
    markBlockingNodes() {
      if (!this.$el || !this.$el.querySelectorAll) return;

      this.$el
        .querySelectorAll(".automation-node--native-blocking")
        .forEach((node) => node.classList.remove("automation-node--native-blocking"));

      const findings = (this.nativeVerdict && this.nativeVerdict.findings) || [];
      findings.forEach((finding) => {
        const node = this.$el.querySelector("#node-" + finding.nodeId);
        if (node) node.classList.add("automation-node--native-blocking");
      });
    },
    // Human labels for the tooltip: "Action <name>: <reason>" / "<node>: <reason>"
    nativeFindingLabel(finding) {
      const block = this.data && this.data.blocks ? this.data.blocks[finding.nodeId] : null;
      let label = block ? block.name : `node ${finding.nodeId}`;

      if (finding.actionId && block && block.data && Array.isArray(block.data.actions)) {
        const action = block.data.actions.find(
          (x) => String(x.id) === String(finding.actionId)
        );
        if (action) label = `action '${action.name || action.type}'`;
      }

      return `${label}: ${finding.reason}`;
    },
    toggleLayout(val) {
      this.fullView = val;
      if (val === false) {
        this.$nextTick(() => {
          this.$el.scrollIntoView();
        });
      }
    },
    editorZoomIn() {
      this.editor && this.editor.zoom_in();
    },
    editorZoomOut() {
      this.editor && this.editor.zoom_out();
    },
    updateEditorZoom(val) {
      this.editor && this.editor.zoom_update(val);
    },
  },
  computed: {
    vectorId() {
      return this.$store.state.vectorId;
    },
    editorZoomPercent() {
      return Boolean(this.editor && this.editor.zoom)
        ? `${Math.trunc(this.editor.zoom * 100)}%`
        : "100%";
    },
    // "serverNative": true on the automation container lets this graph run on the
    // server-native engine when compatible. Master plan ruling (2026-08-01): server-native
    // is the platform DEFAULT — new graphs start with it on, normal users cannot switch a
    // graph back to Python, and internal/consultant users get an explicit warning first
    // (opt-out is for confirmed incompatibility, not preference). Toggling OFF removes the
    // key so designs stay clean.
    serverNativeEnabled: {
      get() {
        return Boolean(this.data) && this.data.serverNative === true;
      },
      set(value) {
        if (!this.data) return;
        if (value) {
          this.$set(this.data, "serverNative", true);
        } else {
          this.relaxServerNative();
        }
      },
    },
    // Who may switch a graph OFF server-native: internal identities and Consultant-role
    // users only (master plan 2026-08-01). Everyone can still switch ON. Identity comes
    // from authorizationStore — the design console's own $store has no identity getters.
    canRelaxServerNative() {
      const roles = authorizationStore.getters.userRoles || [];
      return authorizationStore.getters.isInternal || roles.indexOf("Consultant") > -1;
    },
    // An incompatible graph cannot benefit from opting in, so the toggle locks — and, per
    // the master plan, it also locks ON for users who may not relax it. For relax-capable
    // users it never locks while on (a design edit that breaks compatibility must not trap
    // the flag). No verdict yet (pending/failed) = fail-open.
    serverNativeToggleDisabled() {
      if (this.serverNativeEnabled && !this.canRelaxServerNative) return true;
      return !this.serverNativeEnabled
        && Boolean(this.nativeVerdict)
        && !this.nativeVerdict.compatible;
    },
    nativeVerdictLabel() {
      if (!this.serverNativeEnabled) return "Python engine";
      if (!this.nativeVerdict) return null;
      return this.nativeVerdict.compatible ? "Server-native" : "Python engine";
    },
    nativeVerdictClass() {
      if (!this.serverNativeEnabled) return "automation-native-badge--manual";
      return this.nativeVerdict && this.nativeVerdict.compatible
        ? "automation-native-badge--native"
        : "automation-native-badge--python";
    },
    // every located blocker as a readable line (highlighted nodes carry the same list)
    nativeBlockerSummary() {
      const findings = (this.nativeVerdict && this.nativeVerdict.findings) || [];
      if (!findings.length)
        return this.nativeVerdict && this.nativeVerdict.reason
          ? this.nativeVerdict.reason
          : "";
      return findings.map((f) => this.nativeFindingLabel(f)).join(" • ");
    },
    nativeVerdictTooltip() {
      if (!this.serverNativeEnabled) {
        if (this.nativeVerdict && this.nativeVerdict.compatible)
          return "Compatible with the server-native engine — turn the toggle on to use it";
        if (this.nativeVerdict && !this.nativeVerdict.compatible)
          return `Not compatible — this design needs the Python engine (blocking parts are outlined): ${this.nativeBlockerSummary}`;
        return "Server-native execution is off";
      }
      if (this.nativeVerdict && this.nativeVerdict.compatible)
        return !this.canRelaxServerNative
          ? "This automation runs on the server-native engine. Server-native is the platform default and stays on — contact support if this design misbehaves."
          : "This automation runs on the server-native engine";
      return this.nativeVerdict
        ? `Runs on the Python engine (blocking parts are outlined): ${this.nativeBlockerSummary}`
        : "Runs on the Python engine";
    },
  },
  watch: {
    debugPayload: function (newValue, oldValue) {
      automationDesignStore.dispatch("debugPayload", newValue);
      window.eventBus.$emit("refreshAutomationBlocks");
    },
  },
  async mounted() {
    let vm = this;
    if (!vm.isForm) {
      await vm.loadDiagram();
      // closed while it loaded: beforeDestroy has already run and would never unsubscribe us
      if (vm._isBeingDestroyed || vm._isDestroyed) return;
    }

    // badge tracks every graph edit: the node/action editors all emit this signal
    vm.nativeVerdictRefresh = _.debounce(() => vm.refreshNativeVerdict(), 800);
    window.eventBus.$on("refreshAutomationBlocks", vm.nativeVerdictRefresh);
    vm.nativeVerdictRefresh();
  },
  beforeDestroy() {
    if (this.nativeVerdictRefresh) {
      window.eventBus.$off("refreshAutomationBlocks", this.nativeVerdictRefresh);
    }
    // drawflow mounts each node's component outside this component's vnode tree, so Vue's own teardown
    // never reaches them (or their refreshAutomationBlocks listeners). The canvas leaves with $el.
    if (this.editor) {
      this.editor.destroy();
    }
  },
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-automation-designer {
  height: 800px;
  max-height: 800px;
  display: flex;
  border: 1px solid var(--fts-border);
  background-color: var(--fts-card-background);
  .automation-sidenav {
    flex: 1 1 300px;
    max-width: 300px;
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--fts-border);
    .automation-sidenav__control {
      border-bottom: 1px solid var(--fts-border);
      .automation-debug-selection {
        margin-left: 1rem;
        margin-right: 1rem;
        .automation-debug-select {
          margin-bottom: 1.25rem;
          &:not(:first-child) {
            margin-top: 1.25rem;
          }
        }
      }
      .automation-debug-panel {
        display: flex;
        align-items: center;
        margin: 1rem;
      }
    }
    .automation-sidenav__node {
      border-bottom: 1px solid var(--fts-border);
      .automation-sidenav-node {
        display: flex;
        align-items: center;
        margin: 0 0.75rem 0.575rem;
        padding: 0.375rem 0.75rem;
        border: 1px solid var(--fts-border);
        border-radius: 0.5rem;
        font-weight: 500;
        font-size: 0.875rem;
        line-height: 1.5;
        .automation-sidenav-node__icon {
          background-color: var(--fts-primary);
          color: var(--fts-light);
        }
        &:hover {
          cursor: grab;
        }
        &.automation-sidenav-node-action {
          .automation-sidenav-node__icon {
            background-color: var(--fts-block-action-background);
          }
        }
        &.automation-sidenav-node-condition {
          .automation-sidenav-node__icon {
            background-color: var(--fts-block-condition-background);
          }
        }
        &.automation-sidenav-node-for {
          .automation-sidenav-node__icon {
            background-color: var(--fts-block-for-background);
          }
        }
        .automation-sidenav-node__icon {
          height: 1.5rem;
          width: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 0.375rem;
          border-radius: 0.375rem;
          font-size: 1rem;
        }
      }
    }
    .automation-sidenav__console {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .automation-sidenav-menu {
      display: flex;
      justify-content: space-between;
      padding: 0.75rem 1rem;
      cursor: pointer;
      &:not(.collapsed) {
        .automation-sidenav-menu__toggle {
          transform: rotate(90deg);
        }
      }
      .automation-sidenav-menu__label {
        margin-right: 0.75rem;
        font-size: 0.875rem;
        font-weight: 600;
      }
      .automation-sidenav-menu__toggle {
        flex-shrink: 0;
        transition: all 0.3s;
      }
    }
  }
  .automation-content {
    flex: 1;
    min-width: 400px;
    position: relative;
    background-color: inherit;
    .automation-content-header {
      width: 100%;
      position: absolute;
      top: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.375rem 0.75rem;
      border-bottom: 1px solid var(--fts-border);
      background-color: inherit;
      z-index: 1;
      user-select: none;
    }
    .automation-content-header-action {
      display: flex;
      align-content: center;
      > *:not(:first-child) {
        margin-left: 0.25rem;
      }
    }
    .automation-native-control {
      display: flex;
      align-items: center;
      .automation-native-toggle__label {
        font-size: 0.8125rem;
        white-space: nowrap;
      }
      .automation-native-badge {
        margin-left: 0.5rem;
        padding: 0.125rem 0.5rem;
        font-size: 0.75rem;
        line-height: 1rem;
        white-space: nowrap;
        border-radius: 1rem;
        cursor: default;
        &--native {
          color: var(--fts-success, #2e7d32);
          background-color: rgba(76, 175, 80, 0.12);
        }
        &--python {
          color: var(--fts-warning, #b26a00);
          background-color: rgba(255, 152, 0, 0.12);
        }
        &--manual {
          color: var(--fts-secondary, #6c757d);
          background-color: rgba(108, 117, 125, 0.12);
        }
      }
    }
  }
  .automation-zoom-control {
    display: flex;
    align-items: center;
    .automation-zoom-dropdown {
      margin-left: 0.25rem;
      margin-right: 0.25rem;
    }
    .automation-zoom-dropdown-toggle {
      font-size: 0.875rem;
      line-height: 1.25rem;
    }
  }
  .drawflow {
    /* parts that keep the graph off the server-native engine (verdict findings) */
    .drawflow-node.automation-node--native-blocking {
      outline: 2px solid var(--fts-warning, #ff9800);
      outline-offset: 2px;
      border-radius: 0.5rem;
    }
    .drawflow-node {
      .drawflow_content_node {
        border-radius: inherit;
        > * {
          border-top-left-radius: inherit;
          border-top-right-radius: inherit;
        }
        .node-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 1rem;
          border-bottom: 1px solid var(--fts-border);
          .node-header__label {
            display: flex;
            align-items: center;
            .node-header__label-icon {
              height: 1rem;
              width: 1rem;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 0.375rem;
              border-radius: 0.25rem;
              background-color: var(--fts-primary);
              color: var(--fts-light);
              font-size: 0.75rem;
              &.node-header__label-icon--action {
                background-color: var(--fts-block-action-background);
              }
            }
            .node-header__label-text {
              color: var(--fts-default);
            }
          }
        }
        .node-content {
          display: flex;
          align-items: flex-start;
        }
        .node-content-icon {
          height: 1.125rem;
          width: 1.125rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 0.094rem;
          margin-right: 0.25rem;
          border-radius: 0.25rem;
          background-color: var(--fts-primary);
          color: var(--fts-light);
          font-size: 0.75rem;
          &.node-content-icon--action {
            background-color: var(--fts-block-action-background);
          }
          &.node-content-icon--condition {
            background-color: var(--fts-block-condition-background);
          }
          &.node-content-icon--for {
            background-color: var(--fts-block-for-background);
          }
        }
        .node-content-wrapper {
          flex: 1;
        }
        .node-content-debug {
          position: relative;
          height: 1.313rem;
          width: 1.313rem;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex-shrink: 0;
        }
      }
    }
  }
  .automation-node {
    position: relative; /* anchors the block-number corner tag */
    width: 400px;
    border-radius: inherit;
    background-color: var(--fts-card-background);
    box-shadow: var(--fts-elevation-1);
    &:hover,
    &.automation-node--debug {
      .node-header__debugpoint {
        display: block;
      }
    }
    &.automation-node--debugging {
      outline: 1px solid var(--fts-block-danger);
    }
    .node-header__debugpoint {
      display: none;
    }
  }
  .automation-formula-form-control {
    padding: 0.375rem;
    border: 1px solid var(--fts-form-control-border);
    border-radius: 0.25rem;
    background: var(--fts-form-control-background);
  }
  &.automation-designer--expand {
    position: fixed;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    max-height: 100%;
    background-color: var(--fts-card-background);
    z-index: $z-index-fixed;
  }
  // override class
  .fts-formula-display {
    .formula-display {
      padding-top: 0;
      padding-bottom: 0;
    }
  }
  .fts-expression .formula fts-i-unit {
    padding-top: 0;
    padding-bottom: 0;
  }
}

:root {
  --fts-block-gray-5: #232425;
  --fts-block-danger: #f44336;
  --fts-block-danger-rgb: 244, 67, 54;
  --fts-block-start-background: #00b74a;
  --fts-block-start-background-rgb: 0, 183, 74;
  --fts-block-action-background: #8db600;
  --fts-block-action-background-rgb: 141, 182, 0;
  --fts-block-for-background: #1e88e5;
  --fts-block-for-background-rgb: 30, 136, 229;
  --fts-block-condition-background: #ffa900;
  --fts-block-condition-background-rgb: 255, 169, 0;
  --fts-block-variable-border: #4caf50;
  --fts-block-variable-background: #{rgba(#4caf50, 0.15)};
}
</style>